/**
 * TesoreriaLayout — Sprint 13 S13-08 + Sprint 17 (sidebar completo + mobile drawer).
 *
 * Sub-Layout completo de la vista Tesorería del portal. Cambia toda
 * la UI del portal vecino: sidebar lateral con items operativos +
 * header propio + Outlet para rutas hijas. Es la misma SPA, misma
 * sesión, mismo dominio — solo cambia el shell. Doctrina:
 * docs/dev/doctrina-portal-vs-superadmin.md §3.4.
 *
 * Items del sidebar (actualizado Sprint 19a):
 * - Inicio
 * - Casas
 * - Pagos
 * - Banco
 * - Ingresos varios
 * - Gastos
 * - Compensaciones
 * - Recursos (stock)
 * - Entes externos
 * - Cuotas
 * - Suspensiones (Sprint 19a — hogar en Tesorería; comisión la lee desde
 *   /admin/suspensiones)
 * - Reportes (Sprint 20 — reportería financiera: cobranza, deudores,
 *   tiempo de pago, con descarga XLSX)
 *   (Cierres absorbido en Banco — cuadre anual de flujo de caja.)
 *
 * Sprint 17 — mobile drawer: en pantallas <768px el sidebar pasa a
 * drawer fullscreen con backdrop; el header muestra botón hamburger.
 */
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/auth/AuthContext';
import { FooterVersion } from './FooterVersion';
import { UserMenu } from './UserMenu';

interface SidebarItem {
  to: string;
  label: string;
  proximamente?: boolean;
}

const SIDEBAR_ITEMS: SidebarItem[] = [
  { to: '/tesoreria', label: 'Inicio' },
  { to: '/tesoreria/casas', label: 'Casas' },
  { to: '/tesoreria/pagos', label: 'Pagos' },
  { to: '/tesoreria/banco', label: 'Banco' },
  {
    to: '/tesoreria/ingresos-varios',
    label: 'Ingresos varios',
  },
  { to: '/tesoreria/gastos', label: 'Gastos' },
  { to: '/tesoreria/compensaciones', label: 'Compensaciones' },
  { to: '/tesoreria/recursos', label: 'Recursos' },
  { to: '/tesoreria/entes-externos', label: 'Entes externos' },
  { to: '/tesoreria/cuotas', label: 'Cuotas' },
  { to: '/tesoreria/suspensiones', label: 'Suspensiones' },
  { to: '/tesoreria/reprogramacion', label: 'Reprogramación' },
  { to: '/tesoreria/acceso-wifi', label: 'Acceso WiFi' },
  // "Cierres" absorbido en /tesoreria/banco (cuadre anual de flujo de
  // caja, 2026-06) — el cierre anual y su cuadre viven junto al resto de
  // los datos bancarios, no como sección aparte.
  { to: '/tesoreria/reportes', label: 'Reportes' },
];

interface SidebarProps {
  variant: 'desktop' | 'mobile';
  onNavigate?: () => void;
}

function Sidebar({ variant, onNavigate }: SidebarProps) {
  return (
    <>
      <div className="px-6 py-5 border-b-[0.5px] border-white/[0.08] flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-orange font-medium">
            Tesorería
          </div>
          <div className="text-base text-text-primary font-medium mt-1">
            Barrio Calbucura
          </div>
        </div>
        {variant === 'mobile' && (
          <button
            type="button"
            onClick={onNavigate}
            className="text-text-muted hover:text-text-primary p-1 -mr-1"
            aria-label="Cerrar menú"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="5" y1="5" x2="15" y2="15" />
              <line x1="15" y1="5" x2="5" y2="15" />
            </svg>
          </button>
        )}
      </div>

      <nav
        className="flex-1 px-3 py-4 space-y-1 overflow-y-auto"
        aria-label="Navegación de Tesorería"
      >
        {SIDEBAR_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/tesoreria'}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center justify-between px-3 py-2 rounded text-sm transition-colors duration-base ${
                isActive
                  ? 'bg-bg-card text-text-primary font-medium'
                  : 'text-text-secondary hover:bg-bg-card hover:text-text-primary'
              } ${item.proximamente ? 'opacity-70' : ''}`
            }
            title={item.proximamente ? 'Próximamente' : undefined}
          >
            <span>{item.label}</span>
            {item.proximamente && (
              <span className="text-[10px] uppercase tracking-wide text-text-muted">
                pronto
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="px-6 py-4 border-t-[0.5px] border-white/[0.08] text-xs text-text-muted">
        Portal interno. Las acciones aquí afectan los datos del barrio.
      </div>
    </>
  );
}

export function TesoreriaLayout() {
  const { me } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Cerrar drawer al cambiar de ruta (failsafe; los NavLink ya lo hacen).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Cerrar drawer con ESC.
  useEffect(() => {
    if (!mobileOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMobileOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  if (!me) return null;

  return (
    <div className="min-h-screen bg-bg-page text-text-primary flex">
      {/* Sidebar desktop — visible solo md+ */}
      <aside className="hidden md:flex w-64 bg-bg-sunken border-r-[0.5px] border-white/[0.08] flex-col">
        <Sidebar variant="desktop" />
      </aside>

      {/* Sidebar mobile drawer + backdrop */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-40 md:hidden"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <aside className="fixed inset-y-0 left-0 w-72 max-w-[85vw] bg-bg-sunken border-r-[0.5px] border-white/[0.08] flex flex-col z-50 md:hidden">
            <Sidebar
              variant="mobile"
              onNavigate={() => setMobileOpen(false)}
            />
          </aside>
        </>
      )}

      {/* Contenido principal */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header propio */}
        <header className="bg-bg-card border-b-[0.5px] border-white/[0.08] px-4 md:px-8 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="md:hidden text-text-primary hover:text-orange p-1 -ml-1"
              aria-label="Abrir menú"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="4" y1="6" x2="18" y2="6" />
                <line x1="4" y1="11" x2="18" y2="11" />
                <line x1="4" y1="16" x2="18" y2="16" />
              </svg>
            </button>
            <div className="text-sm text-text-secondary truncate">
              Panel del tesorero
            </div>
          </div>
          {/* Sprint 17 Z17 — consolidado en UserMenu (estándar portal
              vecino desde Sprint 14). Reemplaza RolSelectorCombobox +
              Avatar + nombre + botón "Cerrar sesión" sueltos. */}
          <UserMenu me={me} />
        </header>

        {/* Outlet para rutas hijas */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>

        {/* Footer con versión del módulo Tesorería (S17 patch — antes
            faltaba). FooterVersion detecta módulo por pathname y muestra
            la versión deployada del módulo correspondiente. */}
        <footer className="bg-bg-sunken border-t-[0.5px] border-white/[0.08] px-4 md:px-8 py-3">
          <FooterVersion />
        </footer>
      </div>
    </div>
  );
}
